<script lang="ts" setup>
import { createReusableTemplate, useMediaQuery } from '@vueuse/core'
import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    icon: {
        type: String,
    },
    min: {
        type: Number,
        default: 1
    },
    max: {
        type: Number,
        default: 10000
    },
    btn_class: {
        type: String,
        default: null
    },
})

const minValue = defineModel('minValue')
const maxValue = defineModel('maxValue')

const [UseTemplate, RangeFilter] = createReusableTemplate()
const isDesktop = useMediaQuery('(min-width: 768px)')
const isOpen = ref(false)
const model = ref<number[]>([])
const step = ref<number>(5)

watch(model, (newVal) => {
    if (newVal[0] < props.min) model.value.splice(0, 1, props.min);
    if (newVal[1] > props.max) model.value.splice(1, 1, props.max);
}, {
    deep: true
})

onMounted(() => {
    model.value = [props.min, props.max]
    const computed_step = Math.round((props.max - props.min) / 100)
    step.value = computed_step > 5 ? computed_step : 5
})

function applyFilter(){
    minValue.value = model.value[0]
    maxValue.value = model.value[1]
}

</script>

<template>
    <div>
        <UseTemplate>
            <div class="bg-white rounded border-1 p-4">
                <!-- <div class="labels flex justify-between mb-2 text-sm text-gray-300">
                    <span>{{ model[0] }} AED</span>
                    <span>{{ model[1] }} AED</span>
                </div> -->
                <Slider v-model="model" :default-value="[30]" :max="max" :min="min" :step="step" class="mt-4" />

                <div class="fields_range flex justify-between mt-4">
                    <div class="range_field_input__wrap relative">
                        <Input type="number" :min="min" :max="model[1] - 5" :step="step" v-model="model[0]"
                        :placeholder="$t('Price up')" class="range_field_input bg-stroke max-w-[120px]" />
                        <span class="absolute right-3 rtl:right-auto rtl:left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm font-medium"> AED</span>
                    </div>
                    
                    <div class="range_field_input__wrap relative">
                        <Input type="number" :min="model[0] + 5" :max="max" :step="step" v-model="model[1]"
                        :placeholder="$t('Price to')" class="range_field_input bg-stroke max-w-[120px]" />
                        <span class="absolute right-3 rtl:right-auto rtl:left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm font-medium"> AED</span>
                    </div>
                </div>
                
                <Button @click.prevent="applyFilter" variant="outline" class="w-full mt-4">{{ $t('Apply filter') }}</Button>
            </div>
        </UseTemplate>

        <Popover v-if="isDesktop" v-model:open="isOpen">
            <PopoverTrigger as-child>
                <Button variant="outline" class="w-fit justify-between font-normal text-sub text-sm"
                    :class="props.btn_class ?? 'max-h-[36px] px-2'">
                    <div class="flex">
                        <Icon v-if="icon" size="20" :name="icon" class="mr-2 rtl:ml-2 font-normal"
                            :class="{ 'text-primaryblue-500': model[0] !== min || model[1] !== max }" />
                        <span>{{ label }}</span>
                    </div>
                    <Icon size="18" name="ri:arrow-down-s-line" class="ml-2 rtl:mr-2 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[300px] p-0" align="start">
                <RangeFilter />
            </PopoverContent>
        </Popover>

        <Drawer v-else :open="isOpen" @update:open="(newOpenValue) => isOpen = newOpenValue">
            <DrawerTrigger as-child>
                <Button variant="outline" class="w-[150px] justify-start">
                    {{ label }}
                </Button>
            </DrawerTrigger>
            <DrawerContent>
                <div class="mt-4 border-t">
                    <RangeFilter />
                </div>
            </DrawerContent>
        </Drawer>
    </div>
</template>

<style lang="scss">
input.range_field_input[type=number]::-webkit-outer-spin-button,
input.range_field_input[type=number]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input.range_field_input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>